import { Button as BaseButton } from '@base-ui/react/button';
import type { ComponentProps, ReactNode } from 'react';
import { Shortcut } from '@/components/ui/Shortcut';
import { Spinner } from '@/components/ui/Spinner';
import { cva, cx, type VariantProps } from '@/cva.config';

export const buttonVariants = cva({
  base: 'inline-flex items-center justify-center rounded-md border font-medium transition-all duration-200 ease-out focus-visible:ring-2 focus-visible:ring-accent/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none active:scale-97 disabled:pointer-events-none disabled:opacity-50',
  defaultVariants: {
    size: 'md',
    variant: 'primary',
  },
  variants: {
    fullWidth: {
      true: 'w-full',
    },
    size: {
      lg: 'h-10 gap-2 px-4 text-sm lg:text-base',
      md: 'h-8 gap-1.5 px-3 text-sm lg:text-base',
      sm: 'h-7 gap-1.5 px-2.5 text-xs lg:text-sm',
      xs: 'h-6 gap-1 px-2 text-xs lg:text-sm',
    },
    variant: {
      danger: [
        'border-score-low bg-score-low font-semibold text-white shadow-sm hover:bg-score-low/90',
      ],
      ghost: [
        'border-transparent bg-transparent text-foreground-muted hover:bg-border/30 hover:text-foreground',
      ],
      outline: [
        'border-border bg-transparent text-foreground hover:bg-surface',
      ],
      primary: [
        'border-accent bg-accent text-white shadow-sm hover:-translate-y-px hover:bg-accent/90 hover:shadow-md hover:shadow-accent/20 active:translate-y-0 active:shadow-sm',
      ],
      secondary: [
        'border-border bg-surface text-foreground hover:-translate-y-px hover:bg-border/50 active:translate-y-0',
      ],
      soft: [
        'border-accent bg-accent font-semibold text-white shadow-sm hover:bg-accent/90',
      ],
      success: [
        'border-score-high bg-score-high font-semibold text-background shadow-sm hover:bg-score-high/90',
      ],
    },
  },
});

interface ButtonProps
  extends ComponentProps<typeof BaseButton>,
    VariantProps<typeof buttonVariants> {
  kbd?: string | ReactNode;
  loading?: boolean;
}

export const Button = ({
  children,
  className,
  disabled,
  fullWidth,
  kbd,
  loading = false,
  size,
  variant,
  ...props
}: ButtonProps) => (
  <BaseButton
    className={cx(buttonVariants({ fullWidth, size, variant }), className)}
    {...props}
    aria-busy={loading || undefined}
    disabled={disabled || loading}
  >
    {loading && <Spinner label="" size={size === 'lg' ? 'sm' : 'xs'} />}
    {children}
    {kbd != null
      && (typeof kbd === 'string' ? (
        <Shortcut className="ml-1" keys={kbd} />
      ) : (
        <span className="ml-1">{kbd}</span>
      ))}
  </BaseButton>
);
